import { COLORS } from '../../constants/theme';
import { IntelFreshnessIndicator } from '../attack/IntelFreshnessIndicator';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { getRaceDisplayName } from '../../constants/game';
import { INTEL_TYPE_META } from './intelTypeMeta';
import type { InfiltrateData, SpyOnScienceData, SpyOnHomeDefenseData, SpyOnThroneData, IntelReportDto } from '../../types/intel';

/**
 * SF-157 Component 5 item 3: the four report types that were always view-only (no gather-flow, no
 * `readOnly` split needed) — Infiltrate/Spy on Science/Spy on Home Defense/Spy on Throne. Each
 * gains its own Networth row (Codebase Context — every non-CE record now carries `networth`) and
 * `onShare`/`onDelete` props for Share/Delete parity with the other 4 report types (Component 5's
 * "asymmetry this spec elsewhere explicitly refuses to introduce").
 */

interface CardHeaderProps {
  label: string;
  fg: string;
  bg: string;
  report: IntelReportDto;
  onShare?: () => void;
  onDelete?: () => void;
}

function CardHeader({ label, fg, bg, report, onShare, onDelete }: CardHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: bg, borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ color: fg, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <IntelFreshnessIndicator capturedAt={report.capturedAt} />
        {onShare && report.sharing === 'PRIVATE' && (
          <button
            type="button"
            aria-label={`Share this ${label} report`}
            onClick={onShare}
            style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.positive, cursor: 'pointer', fontSize: 14 }}
          >
            ⤴
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            aria-label={`Delete this ${label} report`}
            onClick={onDelete}
            style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.negative, cursor: 'pointer', fontSize: 14 }}
          >
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

function LV({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: COLORS.text.tertiary, fontSize: 12 }}>{label}</span>
      <span style={{ color: color ?? COLORS.text.primary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

function NetworthRow({ networth }: { networth?: number | null }) {
  if (networth == null) return null;
  return <LV label="Networth" value={formatNumber(networth)} color={COLORS.gold} />;
}

interface DetailCardProps<T> {
  data: T;
  report: IntelReportDto;
  onShare?: () => void;
  onDelete?: () => void;
}

export function MobileInfiltrateCard({ data, report, onShare, onDelete }: DetailCardProps<InfiltrateData>) {
  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      <CardHeader label="Infiltrate" fg={INTEL_TYPE_META.INFILTRATE.fg} bg={INTEL_TYPE_META.INFILTRATE.bg} report={report} onShare={onShare} onDelete={onDelete} />
      <div style={{ padding: 12 }}>
        <LV label="Thieves" value={formatNumber(data.thieves)} />
        <LV label="Raw TPA" value={data.rawTPA.toFixed(2)} />
        <NetworthRow networth={data.networth} />
      </div>
    </div>
  );
}

export function MobileSpyOnScienceCard({ data, report, onShare, onDelete }: DetailCardProps<SpyOnScienceData>) {
  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      <CardHeader label="Spy on Science" fg={INTEL_TYPE_META.SOS.fg} bg={INTEL_TYPE_META.SOS.bg} report={report} onShare={onShare} onDelete={onDelete} />
      <div style={{ padding: 12 }}>
        <LV label="Research Rate" value={formatEnumLabel(data.researchRate)} />
        <NetworthRow networth={data.networth} />
        {data.sciences.length > 0 && (
          <div style={{ marginTop: 8, borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 }}>
            {data.sciences.map((s) => (
              <LV key={s.scienceType} label={formatEnumLabel(s.scienceType)} value={`${formatNumber(s.booksKnown)} books`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MobileSpyOnHomeDefenseCard({ data, report, onShare, onDelete }: DetailCardProps<SpyOnHomeDefenseData>) {
  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      <CardHeader label="Spy on Home Defense" fg={INTEL_TYPE_META.SOHD.fg} bg={INTEL_TYPE_META.SOHD.bg} report={report} onShare={onShare} onDelete={onDelete} />
      <div style={{ padding: 12 }}>
        <LV label="Home Defense" value={formatNumber(Math.round(data.homeDefense))} />
        <NetworthRow networth={data.networth} />
      </div>
    </div>
  );
}

export function MobileSpyOnThroneCard({ data, report, onShare, onDelete }: DetailCardProps<SpyOnThroneData>) {
  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      <CardHeader label="Spy on Throne" fg={INTEL_TYPE_META.SOT.fg} bg={INTEL_TYPE_META.SOT.bg} report={report} onShare={onShare} onDelete={onDelete} />
      <div style={{ padding: 12 }}>
        <LV label="Ruler" value={data.ruler} />
        <LV label="Race" value={getRaceDisplayName(data.race)} />
        <LV label="Thieves" value={formatNumber(data.thieves)} />
        <LV label="Wizards" value={formatNumber(data.wizards)} />
        <LV label="Mana Rating (%)" value={`${(data.manaRating * 100).toFixed(1)}`} />
        <LV label="Net Income/day (gc)" value={formatNumber(Math.round(data.netIncomePerTick))} />
        <NetworthRow networth={data.networth} />
      </div>
    </div>
  );
}
